import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getBookingDetails } from '@/utils/endUserAppApi';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { CheckCircle2, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface BookingDetails {
  id: string;
  bookingNumber: string;

  status: string;
  date: string;
  time: {
    start: string;
    end: string;
  };
  activity: {
    id: string;
    title: string;
    description: string;
  };
  business: {
    id: string;
    name: string;
  };
  customer: {
    name: string;
    email: string;
    phone: string;
  };
  payment: {
    status: string;
    method: string;
    amount: number;
    currency: string;
  };
  participants: Array<{
    type: string;
    quantity: number;
    price: number;
  }>;
  totalParticipants: number;
}

export default function Confirmation() {
  const navigate = useNavigate();
  const { bookingId } = useParams<{ bookingId: string }>();
  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();


  useEffect(() => {
    const fetchBookingDetails = async () => {
      try {
        if (!bookingId) {
          console.error('No bookingId provided');
          return;
        }
        
        const data = await getBookingDetails(bookingId);
        console.log('Received booking data:', data); // Add this log

        // Validate the required fields
        if (!data) {
          console.error('No booking data received');
          throw new Error('No booking data received');
        }

        // Ensure time object exists with fallback
        if (!data.time) {
          console.warn('No time data in booking, using fallback');
          data.time = { start: 'N/A', end: 'N/A' };
        } else {
          // Ensure start and end times exist
          if (!data.time.start) data.time.start = 'N/A';
          if (!data.time.end) data.time.end = 'N/A';
        }

        // Ensure activity object exists with fallback
        if (!data.activity) {
          console.warn('No activity data in booking, using fallback');
          data.activity = { id: '', title: 'Activity', description: '' };
        } else {
          // Check if activity title exists
          if (!data.activity.title) {
            data.activity.title = data.activity.description || 'Activity';
          }
        }

        // Ensure other required fields exist with fallbacks
        if (!data.customer) {
          data.customer = { name: 'N/A', email: 'N/A', phone: 'N/A' };
        }
        if (!data.payment) {
          data.payment = { status: 'unknown', method: 'N/A', amount: 0, currency: 'USD' };
        }
        if (!data.participants) {
          data.participants = [];
        }

        setBooking(data);
      } catch (error) {
        console.error('Error fetching booking details:', error);
        toast({
          title: "Error",
          description: "Failed to load booking details",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [bookingId, toast]);

  // Add debug logging
  useEffect(() => {
    console.log('Current booking state:', booking);
    console.log('Loading state:', loading);
  }, [booking, loading]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#77A146]" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Booking Not Found</h1>
          <p className="mt-2 text-gray-600">The booking details could not be loaded.</p>
          <p className="mt-2 text-sm text-gray-500">Booking ID: {bookingId}</p>
        </div>
      </div>
    );
  }

  // Add data validation before rendering
  const renderTime = () => {
    try {
      return `${booking.time.start} - ${booking.time.end}`;
    } catch (error) {
      console.error('Error rendering time:', error);
      return 'Time not available';
    }
  };


  return (
    <div className="min-h-screen bg-background p-6 md:p-8">
      <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <Button
          onClick={() => navigate('/')}
          variant="outline"
          className="flex items-center gap-2"
        >
          <Home className="h-4 w-4" />

          Back to Home
        </Button>
      </div>
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
          <div className="flex items-center justify-center mb-6">
            <CheckCircle2 className="h-16 w-16 text-green-500" />
          </div>
          
          <h1 className="text-3xl font-bold text-center mb-2">Booking Confirmed!</h1>
          <p className="text-center text-muted-foreground mb-8">
            Your booking has been successfully confirmed. Here are your booking details:
          </p>

          <div className="space-y-6">
            <div className="border-t pt-4">
              <h2 className="text-xl font-semibold mb-4">Booking Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Booking Number</p>
                  <p className="font-medium">{booking.bookingNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Activity</p>
                  <p className="font-medium">{booking.activity.title}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-medium">
                    {format(new Date(booking.date), 'MMMM d, yyyy')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Time</p>
                  <p className="font-medium">{renderTime()}</p>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h2 className="text-xl font-semibold mb-4">Customer Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Name</p>
                  <p className="font-medium">{booking.customer.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{booking.customer.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{booking.customer.phone}</p>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h2 className="text-xl font-semibold mb-4">Payment Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <p className="font-medium capitalize">{booking.payment.status}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Method</p>
                  <p className="font-medium capitalize">{booking.payment.method}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Amount</p>
                  <p className="font-medium">
                    {booking.payment.currency && booking.payment.amount ? 
                      new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: booking.payment.currency
                      }).format(booking.payment.amount) :
                      `$${booking.payment.amount || 0}`
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h2 className="text-xl font-semibold mb-4">Participants</h2>
              <div className="space-y-2">
                {booking.participants.map((participant, index) => (
                  participant.quantity > 0 && (
                    <div key={index} className="flex justify-between">
                      <span className="capitalize">{participant.type}</span>
                      <span>{participant.quantity}</span>
                    </div>
                  )
                ))}
                <div className="flex justify-between font-semibold pt-2 border-t">
                  <span>Total Participants</span>
                  <span>{booking.totalParticipants}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}